import { useQuery } from "@tanstack/react-query";
import { EmptyState, Loading } from "../ui";
import { scholarshipsApi } from "../../api/scholarships";
import { SCHOLARSHIPS_KEY, STATES, VISIBILITIES } from "./model";

const EVENTS = {
  CREATED: "Created",
  UPDATED: "Details changed",
  REQUIREMENTS_REPLACED: "Required documents changed",
  PROGRAMMES_REPLACED: "Programmes changed",
  ELIGIBILITY_REPLACED: "Eligibility changed",
  RUBRIC_REPLACED: "Criteria changed",
  PUBLISHED: "Published",
  UNPUBLISHED: "Unpublished",
  CLOSED: "Closed",
  CANCELLED: "Cancelled",
  DECIDED: "Award list finalised",
  EVALUATOR_ASSIGNED: "Evaluator assigned",
  EVALUATOR_REMOVED: "Evaluator removed",
  SHORTLIST_SENT: "Shortlist sent to the partner",
  SENT_TO_DG: "Award list sent",
  SENT_TO_MINISTER: "Award list sent to the Minister",
  SIGNED_OFF: "Signed off and sent to the partner",
  RETURNED_TO_HEC: "Award list returned to HEC",
  REOPENED: "Reopened after a correction",
  REOPENED_FOR_APPLICATIONS: "Reopened for applications",
};
// Every scholarship is decided once the DG and the Minister, or the sponsor, have decided on each award
const eventLabel = (event) => (event === "DECIDED" ? "Every award decided" : EVENTS[event] ?? event);

const FIELDS = {
  name: "Name",
  description: "Description",
  publicSummary: "Summary",
  type: "Type",
  visibility: "Visibility",
  destinationCountryCode: "Country",
  opensAt: "Opens",
  closesAt: "Closes",
  awardQuota: "Places",
  state: "State",
  evaluators: "Scholarship Review Committee",
  rubric: "Criteria",
  eligibility: "Rules",
  shortlist: "Shortlisted",
  loans: "Sent to the DG",
};

// Lists have no short form
const readable = (value) => (value == null || value === "" ? "—" : typeof value === "object" ? null : String(value).length > 80 ? `${String(value).slice(0, 80)}…` : String(value));
const isDate = (field) => ["opensAt", "closesAt"].includes(field);
// Codes as the screens name them
const labels = (list) => Object.fromEntries(list.map((o) => [o.value, o.label]));
// Entries from before 1 November 2026 may say UNLISTED
const CODES = { visibility: { ...labels(VISIBILITIES), UNLISTED: "Unlisted" }, state: labels(STATES) };
const show = (field, value) =>
  isDate(field) && value
    ? new Date(value).toLocaleString("en-GB", { timeZone: "Africa/Kigali", dateStyle: "medium", timeStyle: "short" })
    : readable(CODES[field]?.[value] ?? value);

// Who changed what and why, newest first
export default function HistoryTab({ scholarship }) {
  const { data = [], isPending, isError, refetch } = useQuery({
    queryKey: [...SCHOLARSHIPS_KEY, "history", scholarship.id, scholarship.version],
    // The API lists the oldest first
    queryFn: async () => [...(await scholarshipsApi.get(`admin/scholarships/${scholarship.id}/history`)).data].reverse(),
  });

  if (isPending) return <Loading label="Loading the history…" />;
  if (isError) return <EmptyState title="Could not load the history" onRefresh={refetch} />;
  if (!data.length) return <EmptyState title="No changes yet" />;

  return (
    <ol className="relative flex flex-col gap-5 border-l border-slate-200 pl-6">
      {data.map((entry, i) => (
        <li key={`${entry.occurredAt}-${i}`} className="relative">
          <span className="absolute -left-[1.85rem] top-1.5 h-3 w-3 rounded-full border-2 border-white bg-brand" />
          <p className="text-sm font-semibold text-slate-800">{eventLabel(entry.event)}</p>
          <p className="text-xs text-slate-500">
            {new Date(entry.occurredAt).toLocaleString("en-GB", { timeZone: "Africa/Kigali", dateStyle: "medium", timeStyle: "short" })}
            {entry.actor?.name && ` · ${entry.actor.name}`}
          </p>
          {entry.changes
            // IDs and lists are summarised by the event itself
            ?.filter((c) => FIELDS[c.field] && show(c.field, c.from) !== null && show(c.field, c.to) !== null)
            .map((c) => (
              <p key={c.field} className="mt-1 text-sm text-slate-600">
                <span className="font-medium">{FIELDS[c.field]}</span>: {show(c.field, c.from)} → {show(c.field, c.to)}
              </p>
            ))}
          {entry.reason && <p className="mt-1 rounded-md bg-slate-50 px-3 py-2 text-sm italic text-slate-600">“{entry.reason}”</p>}
        </li>
      ))}
    </ol>
  );
}
